<template lang="pug">
#other-user-group-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者グループ情報登録

    .box-body.register-body

      FormInputSelect(
        key-name="種類"
        required
        name="group-type"
        v-model="groupTypeId"
        :options="otherGroupTypeOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '540px' }"
      )
      FormInput(
        key-name="グループ名"
        required
        name="other-user-group-name"
        v-model="name"
        type="text"
        placeholder="グループ名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal()" type="button") 登録
        button.btn.cancel-btn(@click="gotoOtherUserGroupList()" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="施設関係者グループ情報を登録しますか？" @ok="registerOtherUserGroup")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoOtherUserGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { OtherUserGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import OtherUserGroupsService, { OtherUserGroupsPostModel } from "@/commons/services/OtherUserGroups";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";

/**
 * 利用者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputSelect
  },
  /**
   * #Topic:
   * バリデーターを使いたいコンポーネントは以下を記載してバリデーターをnewする
   */
  $_veeValidate: { validator: "new" }
})
export default class OtherUserGroupRegister extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** グループ名 */
  name: string = "";
  /** グループ種類 */
  groupTypeId: any = "";
  /** グループ種類名セレクトボックス用項目 */
  otherGroupTypeOptions: Array<{ value: number | string; label: string }> = [];

  /**
   * vue lifecycle created
   */
  public async mounted(): Promise<void> {
    CustomValidate.addCustomValidate(this.$validator);

    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 画面更新がありえるので、初期値を格納する
    this.groupTypeId = "";

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherGroupTypeOptions: Array<{ value: number; label: string }> = this.createOtherUserGroupTypeOptions(otherUserGroupTypes);
    this.otherGroupTypeOptions = otherGroupTypeOptions;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 種類のセレクトボックス用のパラメータを作成する
   */
  public createOtherUserGroupTypeOptions(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Array<{ value: number; label: string }> {
    let otherGroupTypeOptions: Array<{ value: number; label: string }> = [];

    for (const type of otherUserGroupTypes) {
      otherGroupTypeOptions.push({ value: type.id, label: type.name });
    }

    return otherGroupTypeOptions;
  }

  /**
   * ### 登録確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 施設関係者グループ登録
   */
  public async registerOtherUserGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const requestModel: OtherUserGroupsPostModel = {
      name: this.name,
      groupTypeId: this.groupTypeId
    };
    await this.otherUserGroupsService.post(userStore, requestModel);

    // 施設関係者を再読み込みさせるようstoreを更新
    let otherUserGroupList: OtherUserGroupListState = needRefreshesStore.otherUserGroupList;
    otherUserGroupList.otherUserGroupList = true;
    needRefreshesStore.setOtherUserGroupList(otherUserGroupList);
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ一覧に遷移する
   */
  public gotoOtherUserGroupList(): void {
    this.$router.push(PcUrl.OtherUserGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#other-user-group-register {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
